import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { s, authHeaders, Toast } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { MultiChipSelect } from './campaign/MultiChipSelect';
import { RelationPicker } from './ui';
import { RELATION_CONFIGS, relationLabel } from './relationConfigs';
import {
  PERIOD_TYPES, METRIC_TYPES, UNIT_OPTIONS, PRODUCT_SCOPE_TYPES, CUSTOMER_TARGET_TYPES,
  searchCustomerGroups, searchProductGroups, searchProductSubGroups,
  searchVendors, searchVendorGroups, searchRepresentatives, searchRepresentativeGroups,
  periodToDates, periodLabel, itemsToCsv, fmtMoney,
  csvToArr, parseTiers, resolveLabels,
} from './target/targetUtils';
import './TargetWizard.css';

const STEPS = [
  { n: 1, title: 'Kitle & Zamanlama', hint: 'Kime ve ne zaman?' },
  { n: 2, title: 'Hedef & Kapsam', hint: 'Nasıl ve hangi ürünlere?' },
  { n: 3, title: 'Ödül Matrisi', hint: 'Kademeli teşvik' },
  { n: 4, title: 'Özet & Önizleme', hint: 'Dağılım analizi' },
];

const nowYear = new Date().getFullYear();
const genCode = () => `HED-${Date.now().toString(36).toUpperCase().slice(-6)}`;

const EMPTY = () => ({
  code: genCode(),
  name: '',
  description: '',
  // dönem
  periodType: 'MONTH',
  month: `${nowYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
  quarter: 1,
  year: nowYear,
  startDate: '',
  endDate: '',
  // metrik
  metricType: 'AMOUNT',
  unit: 'Koli',
  targetValue: '',
  // ürün kapsamı
  productScopeType: 'ALL',
  // müşteri
  customerTargetType: 'ALL',
});

// ───────────────── küçük yardımcı bileşenler ─────────────────
function Segmented({ options, value, onChange }) {
  return (
    <div className="tw-seg">
      {options.map((o) => (
        <button key={o.value} type="button"
          className={`tw-seg-btn${value === o.value ? ' is-active' : ''}`}
          onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function OptionCards({ options, value, onChange }) {
  return (
    <div className="tw-cards">
      {options.map((o) => (
        <button key={o.value} type="button"
          className={`tw-card-opt${value === o.value ? ' sel' : ''}`}
          onClick={() => onChange(o.value)}>
          <span className="ic">{o.icon}</span>
          <span className="tx">
            <span className="t">{o.label}</span>
            <span className="d">{o.desc}</span>
          </span>
          {value === o.value && <span className="ck">✓</span>}
        </button>
      ))}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div style={s.formGroup}>
      <label style={s.label}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--text-secondary,#9ca3af)' }}>{hint}</span>}
    </div>
  );
}

function Kpi({ label, value, tone }) {
  return (
    <div className={`tw-kpi tw-kpi-${tone || 'blue'}`}>
      <div className="v">{value}</div>
      <div className="l">{label}</div>
    </div>
  );
}

// ISO tarih → yyyy-MM-dd (date input)
const toDateInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// /Select arama fonksiyonundan id listesine karşılık gelen {value,label} öğelerini bul.
async function itemsFromSelect(searchFn, csv) {
  const ids = new Set(csvToArr(csv));
  if (!ids.size) return [];
  try {
    const opts = await searchFn('');
    return opts.filter((o) => ids.has(String(o.value)));
  } catch { return []; }
}

// OData lookup ile id→etiket çözüp {value,label} listesi üret (müşteri/ürün gibi büyük tablolar).
async function itemsFromLookup(endpoint, csv) {
  const ids = csvToArr(csv);
  if (!ids.length) return [];
  try {
    const map = await resolveLabels(endpoint, ids);
    return ids.map((id) => ({ value: id, label: map[id] || id }));
  } catch { return ids.map((id) => ({ value: id, label: id })); }
}

export function TargetWizardPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  // Menü aksiyon hakları: yeni hedef Add, mevcut hedefi güncelleme Update ister.
  const { canAdd, canEdit } = usePageActions();
  const canSave = isEdit ? canEdit : canAdd;
  usePageMeta(isEdit ? 'Hedef Düzenle' : 'Yeni Hedef & Teşvik', 'Çok boyutlu hedef ve kademeli prim kuralları');

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const [loading, setLoading] = useState(isEdit);

  // seçim koleksiyonları (chip listeleri)
  const [custItems, setCustItems] = useState([]);
  const [custGroupItems, setCustGroupItems] = useState([]);
  const [exclItems, setExclItems] = useState([]);
  const [distItems, setDistItems] = useState([]);
  const [distGroupItems, setDistGroupItems] = useState([]);
  const [repItems, setRepItems] = useState([]);
  const [repGroupItems, setRepGroupItems] = useState([]);
  const [prodItems, setProdItems] = useState([]);
  const [prodGroup, setProdGroup] = useState(null);      // {value,label}
  const [prodSubGroup, setProdSubGroup] = useState(null);

  // kademeler
  const [tiers, setTiers] = useState([{ pct: 80, amount: '', gift: '' }, { pct: 100, amount: '', gift: '' }]);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  // ── Düzenleme: mevcut hedefi yükle ve formu/seçimleri/kademeleri doldur ──
  useEffect(() => {
    if (!isEdit) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/targets/${id}`, { headers: authHeaders() });
        const payload = await res.json().catch(() => null);
        if (!res.ok) throw new Error((payload && (payload.message || payload.Message)) || 'Hedef yüklenemedi');
        const t = payload?.data ?? payload?.Data ?? payload;
        if (!t || !alive) return;

        const periodType = (t.periodType || t.PeriodType || 'MONTH').toUpperCase();
        const startIso = t.startDate ?? t.StartDate ?? null;
        const endIso = t.endDate ?? t.EndDate ?? null;
        const sd = startIso ? new Date(startIso) : null;
        const metricType = (t.metricType || t.MetricType || 'AMOUNT').toUpperCase();
        const unitRaw = t.unit ?? t.Unit ?? '';

        setForm({
          code: t.code ?? t.Code ?? genCode(),
          name: t.name ?? t.Name ?? '',
          description: t.description ?? t.Description ?? '',
          periodType,
          month: periodType === 'MONTH' && sd ? `${sd.getFullYear()}-${String(sd.getMonth() + 1).padStart(2, '0')}` : `${nowYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
          quarter: periodType === 'QUARTER' && sd ? Math.floor(sd.getMonth() / 3) + 1 : 1,
          year: sd ? sd.getFullYear() : nowYear,
          startDate: periodType === 'CUSTOM' ? toDateInput(startIso) : '',
          endDate: periodType === 'CUSTOM' ? toDateInput(endIso) : '',
          metricType,
          unit: metricType === 'QUANTITY' && UNIT_OPTIONS.includes(unitRaw) ? unitRaw : 'Koli',
          targetValue: String(t.targetValue ?? t.TargetValue ?? ''),
          productScopeType: (t.productScopeType || t.ProductScopeType || 'ALL').toUpperCase(),
          customerTargetType: (t.customerTargetType || t.CustomerTargetType || 'ALL').toUpperCase(),
        });

        // kademeler
        const loadedTiers = parseTiers(t.tiersJson ?? t.TiersJson).map((x) => ({
          pct: x.pct ?? '', amount: x.amount ? String(x.amount) : '', gift: x.gift || '',
        }));
        if (loadedTiers.length) setTiers(loadedTiers);

        // chip/seçim koleksiyonlarını paralel çöz
        const groupId = t.applicableGroupId ?? t.ApplicableGroupId;
        const subGroupId = t.applicableSubGroupId ?? t.ApplicableSubGroupId;
        const [
          cust, custGroup, excl, dist, distGroup, rep, repGroup, prod, pGroup, pSub,
        ] = await Promise.all([
          itemsFromLookup('/api/customers', t.targetCustomerIdsCsv ?? t.TargetCustomerIdsCsv),
          itemsFromSelect(searchCustomerGroups, t.targetCustomerGroupIdsCsv ?? t.TargetCustomerGroupIdsCsv),
          itemsFromLookup('/api/customers', t.excludedCustomerIdsCsv ?? t.ExcludedCustomerIdsCsv),
          itemsFromSelect(searchVendors, t.distributorIdsCsv ?? t.DistributorIdsCsv),
          itemsFromSelect(searchVendorGroups, t.distributorGroupIdsCsv ?? t.DistributorGroupIdsCsv),
          itemsFromSelect(searchRepresentatives, t.representativeIdsCsv ?? t.RepresentativeIdsCsv),
          itemsFromSelect(searchRepresentativeGroups, t.representativeGroupIdsCsv ?? t.RepresentativeGroupIdsCsv),
          itemsFromLookup('/api/products', t.applicableProductIdsCsv ?? t.ApplicableProductIdsCsv),
          groupId ? itemsFromSelect(searchProductGroups, String(groupId)) : Promise.resolve([]),
          subGroupId ? itemsFromSelect(searchProductSubGroups, String(subGroupId)) : Promise.resolve([]),
        ]);
        if (!alive) return;
        setCustItems(cust); setCustGroupItems(custGroup); setExclItems(excl);
        setDistItems(dist); setDistGroupItems(distGroup); setRepItems(rep); setRepGroupItems(repGroup);
        setProdItems(prod);
        setProdGroup(pGroup[0] || null);
        setProdSubGroup(pSub[0] || null);
      } catch (e) {
        if (alive) showToast('error', e.message || 'Hedef yüklenemedi');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  // ── kademe yönetimi ──
  const addTier = () => setTiers((t) => [...t, { pct: '', amount: '', gift: '' }]);
  const removeTier = (i) => setTiers((t) => t.filter((_, idx) => idx !== i));
  const updateTier = (i, patch) => setTiers((t) => t.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  // ── payload üretimi ──
  const buildPayload = useCallback(() => {
    const { startDate, endDate } = periodToDates(form);
    const cleanTiers = tiers
      .filter((t) => t.pct !== '' && (t.amount !== '' || (t.gift || '').trim() !== ''))
      .map((t) => ({ pct: Number(t.pct), amount: Number(t.amount || 0), gift: (t.gift || '').trim() }));
    return {
      code: form.code, name: form.name.trim(), description: form.description || null, isActive: true,
      periodType: form.periodType, startDate, endDate,
      metricType: form.metricType,
      unit: form.metricType === 'AMOUNT' ? '₺' : form.unit,
      targetValue: Number(form.targetValue || 0),
      productScopeType: form.productScopeType,
      applicableProductIdsCsv: form.productScopeType === 'SKU' ? itemsToCsv(prodItems) : null,
      applicableGroupId: form.productScopeType === 'GROUP' ? (prodGroup?.value ?? null) : null,
      applicableSubGroupId: form.productScopeType === 'SUBGROUP' ? (prodSubGroup?.value ?? null) : null,
      customerTargetType: form.customerTargetType,
      targetCustomerIdsCsv: ['SINGLE', 'MULTI'].includes(form.customerTargetType) ? itemsToCsv(custItems) : null,
      targetCustomerGroupIdsCsv: form.customerTargetType === 'GROUP' ? itemsToCsv(custGroupItems) : null,
      excludedCustomerIdsCsv: itemsToCsv(exclItems) || null,
      distributorIdsCsv: itemsToCsv(distItems) || null,
      distributorGroupIdsCsv: itemsToCsv(distGroupItems) || null,
      representativeIdsCsv: itemsToCsv(repItems) || null,
      representativeGroupIdsCsv: itemsToCsv(repGroupItems) || null,
      tiersJson: JSON.stringify(cleanTiers),
    };
  }, [form, tiers, custItems, custGroupItems, exclItems, distItems, distGroupItems, repItems, repGroupItems, prodItems, prodGroup, prodSubGroup]);

  const buildPreviewBody = () => {
    const p = buildPayload();
    return {
      customerTargetType: p.customerTargetType,
      targetCustomerIdsCsv: p.targetCustomerIdsCsv,
      targetCustomerGroupIdsCsv: p.targetCustomerGroupIdsCsv,
      excludedCustomerIdsCsv: p.excludedCustomerIdsCsv,
      distributorIdsCsv: p.distributorIdsCsv,
      distributorGroupIdsCsv: p.distributorGroupIdsCsv,
      representativeIdsCsv: p.representativeIdsCsv,
      representativeGroupIdsCsv: p.representativeGroupIdsCsv,
    };
  };

  // ── adım doğrulama ──
  const validateStep = (n) => {
    if (n === 1) {
      if (!form.name.trim()) return 'Hedef adı zorunlu.';
      if (form.periodType === 'MONTH' && !form.month) return 'Ay seçin.';
      if (form.periodType === 'CUSTOM' && (!form.startDate || !form.endDate)) return 'Başlangıç ve bitiş tarihi seçin.';
      if (form.periodType === 'CUSTOM' && form.endDate < form.startDate) return 'Bitiş tarihi başlangıçtan önce olamaz.';
      if (['SINGLE', 'MULTI'].includes(form.customerTargetType) && custItems.length === 0) return 'En az bir müşteri seçin.';
      if (form.customerTargetType === 'GROUP' && custGroupItems.length === 0) return 'En az bir müşteri grubu seçin.';
    }
    if (n === 2) {
      if (!(Number(form.targetValue) > 0)) return 'Hedef değeri 0\'dan büyük olmalı.';
      if (form.productScopeType === 'GROUP' && !prodGroup) return 'Bir ürün grubu seçin.';
      if (form.productScopeType === 'SUBGROUP' && !prodSubGroup) return 'Bir ürün alt grubu seçin.';
      if (form.productScopeType === 'SKU' && prodItems.length === 0) return 'En az bir ürün (SKU) seçin.';
    }
    if (n === 3) {
      const rows = tiers.filter((t) => t.pct !== '' && (t.amount !== '' || (t.gift || '').trim() !== ''));
      if (rows.length === 0) return 'En az bir ödül kademesi tanımlayın (eşik + tutar/hediye).';
      let prev = -1;
      for (const t of rows) {
        const pct = Number(t.pct);
        if (Number.isNaN(pct) || pct < 0) return 'Kademe eşiği geçersiz.';
        if (pct <= prev) return 'Kademe eşikleri artan sırada olmalı.';
        prev = pct;
      }
    }
    return null;
  };

  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const res = await fetch('/api/targets/preview', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(buildPreviewBody()),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error((payload && (payload.message || payload.Message)) || 'Önizleme alınamadı');
      setPreview(payload?.data ?? payload?.Data ?? null);
    } catch (e) { showToast('error', e.message); setPreview(null); }
    finally { setPreviewLoading(false); }
  }, [buildPreviewBody]);

  const goNext = () => {
    const err = validateStep(step);
    if (err) { showToast('error', err); return; }
    const next = Math.min(4, step + 1);
    setStep(next);
    if (next === 4) fetchPreview();
  };
  const goBack = () => setStep((sp) => Math.max(1, sp - 1));

  const save = async () => {
    for (let n = 1; n <= 3; n++) { const e = validateStep(n); if (e) { showToast('error', e); setStep(n); return; } }
    setSaving(true);
    try {
      const url = isEdit ? `/api/targets/${id}` : '/api/targets';
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(buildPayload()) });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error((payload && (payload.message || payload.Message)) || 'Kayıt başarısız');
      // listeye dön (kayıt başarılı toast'ını liste değil burada gösteremeyiz; navigasyon yeterli)
      navigate('/targets');
    } catch (e) { showToast('error', e.message); setSaving(false); }
  };

  const audienceSummary = useMemo(() => {
    const ct = CUSTOMER_TARGET_TYPES.find((t) => t.value === form.customerTargetType)?.label || '-';
    const sf = [];
    if (distItems.length) sf.push(`${distItems.length} distribütör`);
    if (distGroupItems.length) sf.push(`${distGroupItems.length} dist. grubu`);
    if (repGroupItems.length) sf.push(`${repGroupItems.length} temsilci grubu`);
    if (repItems.length) sf.push(`${repItems.length} temsilci`);
    return { ct, sf: sf.length ? sf.join(', ') : 'Tüm saha' };
  }, [form.customerTargetType, distItems, distGroupItems, repItems, repGroupItems]);

  if (loading) {
    return (
      <div style={s.container} className="tw-root">
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary,#9ca3af)' }}>Hedef yükleniyor…</div>
      </div>
    );
  }

  return (
    <div style={s.container} className="tw-root">
      {/* başlık */}
      <div className="tw-head">
        <div>
          <button type="button" style={{ ...s.btnSecondary, marginBottom: 10 }} onClick={() => navigate('/targets')}>← Listeye Dön</button>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--text-primary,#0f172a)' }}>{isEdit ? 'Hedef Düzenle' : 'Yeni Hedef & Teşvik'}</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary,#64748b)' }}>
            Sahayı motive etmek için çok boyutlu hedef ve kademeli prim kuralı kurun.
          </p>
        </div>
      </div>

      {/* stepper */}
      <div className="tw-stepbar">
        {STEPS.map((st, i) => (
          <div key={st.n} className={`tw-step${step === st.n ? ' is-active' : step > st.n ? ' is-done' : ''}`}
            onClick={() => { if (st.n < step) setStep(st.n); }}>
            <span className="tw-step-no">{step > st.n ? '✓' : st.n}</span>
            <span className="tw-step-tx">
              <span className="tw-step-t">{st.title}</span>
              <span className="tw-step-h">{st.hint}</span>
            </span>
            {i < STEPS.length - 1 && <span className="tw-step-sep">›</span>}
          </div>
        ))}
      </div>

      {/* gövde */}
      <div className="tw-body">
        {step === 1 && (
          <div className="tw-grid2">
            <div className="tw-card">
              <h4 className="tw-card-h">Kimlik</h4>
              <div style={s.formRow}>
                <Field label="Hedef Adı *"><input style={s.input} value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Örn: Haziran Ciro Hedefi" /></Field>
                <Field label="Kod"><input style={s.input} value={form.code} onChange={(e) => set({ code: e.target.value })} /></Field>
              </div>
              <Field label="Açıklama"><textarea style={{ ...s.input, minHeight: 60, resize: 'vertical' }} value={form.description} onChange={(e) => set({ description: e.target.value })} /></Field>

              <h4 className="tw-card-h" style={{ marginTop: 16 }}>Dönem</h4>
              <Segmented options={PERIOD_TYPES} value={form.periodType} onChange={(v) => set({ periodType: v })} />
              <div style={{ marginTop: 10 }}>
                {form.periodType === 'MONTH' && (
                  <Field label="Ay"><input type="month" style={s.input} value={form.month} onChange={(e) => set({ month: e.target.value })} /></Field>
                )}
                {form.periodType === 'QUARTER' && (
                  <div style={s.formRow}>
                    <Field label="Çeyrek">
                      <Segmented options={[1, 2, 3, 4].map((q) => ({ value: q, label: `Ç${q}` }))} value={form.quarter} onChange={(v) => set({ quarter: v })} />
                    </Field>
                    <Field label="Yıl"><input type="number" style={s.input} value={form.year} onChange={(e) => set({ year: Number(e.target.value) })} /></Field>
                  </div>
                )}
                {form.periodType === 'CUSTOM' && (
                  <div style={s.formRow}>
                    <Field label="Başlangıç"><input type="date" style={s.input} value={form.startDate} onChange={(e) => set({ startDate: e.target.value })} /></Field>
                    <Field label="Bitiş"><input type="date" style={s.input} value={form.endDate} onChange={(e) => set({ endDate: e.target.value })} /></Field>
                  </div>
                )}
              </div>
            </div>

            <div className="tw-card">
              <h4 className="tw-card-h">Müşteri Kitlesi (Kime?)</h4>
              <OptionCards options={CUSTOMER_TARGET_TYPES} value={form.customerTargetType} onChange={(v) => set({ customerTargetType: v })} />
              <div style={{ marginTop: 10 }}>
                {['SINGLE', 'MULTI'].includes(form.customerTargetType) && (
                  <Field label="Müşteriler"><MultiChipSelect items={custItems} onChange={setCustItems} config={RELATION_CONFIGS.customer} placeholder="Müşteri ara…" /></Field>
                )}
                {form.customerTargetType === 'GROUP' && (
                  <Field label="Müşteri Grupları"><MultiChipSelect items={custGroupItems} onChange={setCustGroupItems} config={RELATION_CONFIGS.customerGroup} placeholder="Grup ara…" /></Field>
                )}
                <Field label="Hariç Tutulanlar (opsiyonel)"><MultiChipSelect items={exclItems} onChange={setExclItems} config={RELATION_CONFIGS.customer} placeholder="Hariç müşteri ara…" /></Field>
              </div>

              <h4 className="tw-card-h" style={{ marginTop: 16 }}>Saha Kitlesi (opsiyonel · boş = tümü)</h4>
              <div style={s.formRow}>
                <Field label="Distribütör"><MultiChipSelect items={distItems} onChange={setDistItems} config={RELATION_CONFIGS.vendor} placeholder="Distribütör ara…" /></Field>
                <Field label="Distribütör Grubu"><MultiChipSelect items={distGroupItems} onChange={setDistGroupItems} config={RELATION_CONFIGS.vendorGroup} placeholder="Grup ara…" /></Field>
              </div>
              <div style={s.formRow}>
                <Field label="Temsilci Grubu"><MultiChipSelect items={repGroupItems} onChange={setRepGroupItems} config={RELATION_CONFIGS.representativeGroup} placeholder="Temsilci grubu ara…" /></Field>
                <Field label="Tekil Temsilci"><MultiChipSelect items={repItems} onChange={setRepItems} config={RELATION_CONFIGS.representative} placeholder="Temsilci ara…" /></Field>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="tw-grid2">
            <div className="tw-card">
              <h4 className="tw-card-h">Hedef Tipi (Nasıl ölçülür?)</h4>
              <OptionCards options={METRIC_TYPES} value={form.metricType} onChange={(v) => set({ metricType: v })} />
              <div style={{ ...s.formRow, marginTop: 10 }}>
                <Field label={`Hedef Değeri ${form.metricType === 'AMOUNT' ? '(₺)' : `(${form.unit})`}`}>
                  <input type="number" min="0" style={s.input} value={form.targetValue} onChange={(e) => set({ targetValue: e.target.value })} placeholder="Örn: 1000000" />
                </Field>
                {form.metricType === 'QUANTITY' && (
                  <Field label="Birim">
                    <select style={s.input} value={form.unit} onChange={(e) => set({ unit: e.target.value })}>
                      {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </Field>
                )}
              </div>
            </div>

            <div className="tw-card">
              <h4 className="tw-card-h">Ürün Kapsamı (Hangi ürünlere?)</h4>
              <OptionCards options={PRODUCT_SCOPE_TYPES} value={form.productScopeType} onChange={(v) => set({ productScopeType: v })} />
              <div style={{ marginTop: 10 }}>
                {form.productScopeType === 'GROUP' && (
                  <Field label="Ürün Grubu"><RelationPicker config={RELATION_CONFIGS.productGroup} value={prodGroup?.value || ''} selectedLabel={prodGroup?.label} onChange={(v, it) => setProdGroup(v ? { value: v, label: it ? relationLabel(it) : '' } : null)} /></Field>
                )}
                {form.productScopeType === 'SUBGROUP' && (
                  <Field label="Ürün Alt Grubu"><RelationPicker config={RELATION_CONFIGS.productSubGroup} value={prodSubGroup?.value || ''} selectedLabel={prodSubGroup?.label} onChange={(v, it) => setProdSubGroup(v ? { value: v, label: it ? relationLabel(it) : '' } : null)} /></Field>
                )}
                {form.productScopeType === 'SKU' && (
                  <Field label="Ürünler (SKU)"><MultiChipSelect items={prodItems} onChange={setProdItems} config={RELATION_CONFIGS.product} placeholder="Ürün ara…" /></Field>
                )}
                {form.productScopeType === 'ALL' && (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary,#9ca3af)' }}>Tüm ürünler kapsama dahil.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="tw-card">
            <h4 className="tw-card-h">Kademeli Ödül / Teşvik Matrisi</h4>
            <p style={{ fontSize: 12, color: 'var(--text-secondary,#9ca3af)', margin: '0 0 12px' }}>
              Gerçekleşme eşiğine göre prim. Örn: %80 → 5.000 ₺, %100 → 20.000 ₺ / tablet. Eşikler artan olmalı.
            </p>
            <table className="tw-tier-table">
              <thead>
                <tr><th>Eşik (%)</th><th>Ödül (₺)</th><th>Hediye (opsiyonel)</th><th></th></tr>
              </thead>
              <tbody>
                {tiers.map((t, i) => (
                  <tr key={i}>
                    <td><input type="number" min="0" style={s.input} value={t.pct} onChange={(e) => updateTier(i, { pct: e.target.value })} placeholder="80" /></td>
                    <td><input type="number" min="0" style={s.input} value={t.amount} onChange={(e) => updateTier(i, { amount: e.target.value })} placeholder="5000" /></td>
                    <td><input style={s.input} value={t.gift} onChange={(e) => updateTier(i, { gift: e.target.value })} placeholder="Örn: tablet" /></td>
                    <td><button type="button" className="tw-tier-del" onClick={() => removeTier(i)} title="Sil">✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" style={{ ...s.btnSecondary, marginTop: 12 }} onClick={addTier}>+ Kademe Ekle</button>
          </div>
        )}

        {step === 4 && (
          <div className="tw-grid2">
            <div className="tw-card">
              <h4 className="tw-card-h">Dağılım Önizleme (Canlı)</h4>
              {previewLoading ? (
                <p style={{ color: 'var(--text-secondary,#9ca3af)' }}>Hesaplanıyor…</p>
              ) : (
                <>
                  <div className="tw-kpis">
                    <Kpi label="Etkilenen Müşteri" value={preview?.affectedCustomers ?? '—'} tone="blue" />
                    <Kpi label="Etkilenen Temsilci" value={preview?.affectedRepresentatives ?? '—'} tone="green" />
                    <Kpi label="Distribütör" value={preview?.affectedDistributors ?? '—'} tone="purple" />
                  </div>
                  <div className="tw-samples">
                    <div>
                      <div className="tw-samp-h">Örnek Müşteriler</div>
                      {(preview?.sampleCustomers || []).map((x) => <div key={x.id} className="tw-samp-row">{x.label}</div>)}
                      {!preview?.sampleCustomers?.length && <div className="tw-samp-empty">—</div>}
                    </div>
                    <div>
                      <div className="tw-samp-h">Örnek Temsilciler</div>
                      {(preview?.sampleRepresentatives || []).map((x) => <div key={x.id} className="tw-samp-row">{x.label}</div>)}
                      {!preview?.sampleRepresentatives?.length && <div className="tw-samp-empty">—</div>}
                    </div>
                  </div>
                  <button type="button" style={{ ...s.btnSecondary, marginTop: 12 }} onClick={fetchPreview}>↻ Yenile</button>
                </>
              )}
            </div>

            <div className="tw-card">
              <h4 className="tw-card-h">Kural Özeti</h4>
              <ul className="tw-summary">
                <li><b>Hedef:</b> {form.name || '—'} <span className="tw-mut">({form.code})</span></li>
                <li><b>Dönem:</b> {periodLabel(form)}</li>
                <li><b>Metrik:</b> {form.metricType === 'AMOUNT' ? 'TL / Ciro' : `Ürün Birimi (${form.unit})`} · Hedef: {fmtMoney(form.targetValue)} {form.metricType === 'AMOUNT' ? '₺' : form.unit}</li>
                <li><b>Ürün Kapsamı:</b> {PRODUCT_SCOPE_TYPES.find((p) => p.value === form.productScopeType)?.label}</li>
                <li><b>Müşteri:</b> {audienceSummary.ct}{exclItems.length ? ` · ${exclItems.length} hariç` : ''}</li>
                <li><b>Saha:</b> {audienceSummary.sf}</li>
                <li><b>Kademeler:</b>
                  <div className="tw-tier-chips">
                    {tiers.filter((t) => t.pct !== '').map((t, i) => (
                      <span key={i} className="tw-tier-chip">%{t.pct} → {t.amount ? `${fmtMoney(t.amount)}₺` : ''}{t.gift ? ` ${t.gift}` : ''}</span>
                    ))}
                  </div>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* footer */}
      <div className="tw-footer">
        <button type="button" style={s.btnSecondary} onClick={goBack} disabled={step === 1}>← Geri</button>
        <div style={{ flex: 1 }} />
        {step < 4
          ? <button type="button" style={s.btnPrimary} onClick={goNext}>İleri →</button>
          : canSave && <button type="button" style={{ ...s.btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={save} disabled={saving}>{saving ? 'Kaydediliyor…' : (isEdit ? '✓ Değişiklikleri Kaydet' : '✓ Hedefi Kaydet')}</button>}
      </div>

      <Toast toast={toast} />
    </div>
  );
}
